import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Card from '@components/Card';
import getPriceWithDiscountAndPromotions from '@utils/price';
import { PRODUCT_CLICK_LOCATION } from '@conversion/constants';
import { onKeyUpFunction, onKeyDownFunction } from '@utils/a11y';
import ProductPrice from './ProductItemPrice';
import Image from './Image';

const StyledCard = styled(Card)`
  width: 301px;
  height: 130px;
  box-sizing: border-box;
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 8)};
  position: relative;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 220px;
`;

const Text = styled.span`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({ principal, hasDescription }) =>
    principal && hasDescription ? 1 : 2};
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${({ principal }) =>
    principal &&
    css`
      ${({ theme }) => theme.fontFamily.secondaryFont.mediumRegular};
      color: ${({ theme }) => theme.colors.night_blue_1};
    `}

  ${({ secondary }) =>
    secondary &&
    css`
       ${({ theme }) => theme.fontFamily.secondaryFont.xSmallRegular}
       color: ${({ theme }) => theme.colors.night_blue_2};
    `}
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DiscountProduct = ({ onClick, product, menuSection }) => {
  const {
    name,
    price,
    description,
    priceWithDiscount,
    subsidizedProduct,
    image,
    finalPriceWithoutTaxes,
  } = product;

  const handleClick = evt => {
    const clickLocation =
      (evt && evt.target.id) || PRODUCT_CLICK_LOCATION.INFOCARD;
    onClick(product, clickLocation, menuSection);
  };

  return (
    <StyledCard
      onClick={handleClick}
      onKeyUp={e => onKeyUpFunction(e, () => onClick({ e }))}
      onKeyDown={e => onKeyDownFunction(e, () => onClick({ e }))}
      tabIndex={0}
    >
      <InfoContainer>
        <Text principal hasDescription={description}>
          {name}
        </Text>
        <Text secondary>{description}</Text>
        <ProductPrice
          price={price}
          priceWithDiscount={getPriceWithDiscountAndPromotions({
            priceWithDiscount,
          })}
          subsidizedProduct={subsidizedProduct}
          finalPriceWithoutTaxes={finalPriceWithoutTaxes}
        />
      </InfoContainer>
      <ImageContainer>
        {image && <Image name={name} image={image} />}
      </ImageContainer>
    </StyledCard>
  );
};

DiscountProduct.propTypes = {
  onClick: PropTypes.func.isRequired,
  product: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    applyDiscount: PropTypes.bool,
    price: PropTypes.number,
    priceWithDiscount: PropTypes.number,
    finalPriceWithoutTaxes: PropTypes.number,
    subsidizedProduct: PropTypes.object,
    image: PropTypes.string,
  }).isRequired,
  menuSection: PropTypes.string,
};

export default memo(DiscountProduct);
